import type { ArchetypePassive, ElementType } from '../../types';
import type { PlayerStats, EngineTechnique } from '../data/EngineData';
import type { ActionType } from './ActionCandidate';

export type PassiveActionType = ActionType | 'SAVE' | 'DUEL';

export interface PassivePlayer {
  element: ElementType;
  stats: PlayerStats;
  passive: ArchetypePassive | null;
}

export interface PassiveContext {
  minute: number;
  scoreDiff: number;
  secondsSincePossessionGain: number;
  secondsSinceConceded: number;
  isDecisive?: boolean;
  isVerticalAction?: boolean;
}

export interface PassiveModifierParams {
  player: PassivePlayer;
  opponent?: PassivePlayer;
  actionType: PassiveActionType;
  baseScore: number;
  context: PassiveContext;
  technique?: EngineTechnique;
  elementMultiplier?: number;
}

export interface PassiveModifierResult {
  score: number;
  staminaCostMultiplier: number;
  foulRiskMultiplier: number;
  techniqueAggressionMultiplier: number;
  movementSpeedMultiplier: number;
  debugReasons: string[];
}

export interface PassiveSystemConfig {
  affinityAdvantageMultiplier: number;
  affinityTechniqueMultiplier: number;
  breachGap: number;
  breachMultiplier: number;
  counterWindowSeconds: number;
  counterActionMultiplier: number;
  counterRunSpeedMultiplier: number;
  dirtyDuelMultiplier: number;
  dirtyPressMultiplier: number;
  dirtyFoulRiskMultiplier: number;
  justiceDefensiveMultiplier: number;
  justiceConcededWindowSeconds: number;
  justiceVsDirtyMultiplier: number;
  tensionMinute: number;
  tensionActionMultiplier: number;
  tensionTechniqueAggressionMultiplier: number;
  tensionStaminaCostMultiplier: number;
}

export const DEFAULT_PASSIVE_CONFIG: PassiveSystemConfig = {
  affinityAdvantageMultiplier: 1.08,
  affinityTechniqueMultiplier: 1.04,
  breachGap: 15,
  breachMultiplier: 1.07,
  counterWindowSeconds: 6,
  counterActionMultiplier: 1.08,
  counterRunSpeedMultiplier: 1.10,
  dirtyDuelMultiplier: 1.10,
  dirtyPressMultiplier: 1.08,
  dirtyFoulRiskMultiplier: 1.08,
  justiceDefensiveMultiplier: 1.08,
  justiceConcededWindowSeconds: 8,
  justiceVsDirtyMultiplier: 1.05,
  tensionMinute: 75,
  tensionActionMultiplier: 1.08,
  tensionTechniqueAggressionMultiplier: 1.12,
  tensionStaminaCostMultiplier: 1.05,
};

const NORMALIZED_ARCHETYPES: Record<string, ArchetypePassive> = {
  afinidad: 'AFFINITY',
  brecha: 'BREACH',
  contraataque: 'COUNTER',
  'juego sucio': 'DIRTY_PLAY',
  justicia: 'JUSTICE',
  tension: 'TENSION',
};

export const ARCHETYPE_TO_PASSIVE: Record<string, ArchetypePassive> = {
  Afinidad: 'AFFINITY',
  Brecha: 'BREACH',
  Contraataque: 'COUNTER',
  'Juego sucio': 'DIRTY_PLAY',
  Justicia: 'JUSTICE',
  'Tensión': 'TENSION',
};

export function mapArchetypeToPassive(archetype: string | undefined): ArchetypePassive | null {
  if (!archetype) return null;
  const normalized = archetype.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase();
  return NORMALIZED_ARCHETYPES[normalized] ?? null;
}

const COUNTER_ACTIONS: readonly PassiveActionType[] = ['PASS', 'DRIBBLE', 'CARRY', 'SHOOT', 'MAKE_RUN', 'USE_TECHNIQUE'];
const DIRTY_DUELS: readonly PassiveActionType[] = ['TACKLE', 'INTERCEPT', 'DUEL'];
const DEFENSIVE_ACTIONS: readonly PassiveActionType[] = ['TACKLE', 'INTERCEPT', 'PRESS', 'MARK_PLAYER', 'CLEAR_BALL', 'SAVE', 'DUEL'];
const IMPORTANT_ACTIONS: readonly PassiveActionType[] = ['SHOOT', 'SAVE', 'USE_TECHNIQUE', 'TACKLE', 'INTERCEPT', 'DUEL'];

export class PassiveSystem {
  constructor(readonly config: PassiveSystemConfig = DEFAULT_PASSIVE_CONFIG) {}

  applyPassiveModifier(params: PassiveModifierParams): PassiveModifierResult {
    const result: PassiveModifierResult = {
      score: params.baseScore,
      staminaCostMultiplier: 1,
      foulRiskMultiplier: 1,
      techniqueAggressionMultiplier: 1,
      movementSpeedMultiplier: 1,
      debugReasons: [],
    };
    const passive = params.player.passive;
    if (!passive) return result;
    const effectiveAction: PassiveActionType = params.actionType === 'USE_TECHNIQUE'
      ? params.technique?.type === 'SHOT' ? 'SHOOT'
        : params.technique?.type === 'PASS' ? 'PASS'
          : params.technique?.type === 'DRIBBLE' ? 'DRIBBLE'
            : params.technique?.type === 'BLOCK' ? 'TACKLE'
              : params.technique?.type === 'CATCH' ? 'SAVE' : params.actionType
      : params.actionType;

    const applyScore = (multiplier: number, reason: string): void => {
      result.score *= multiplier;
      result.debugReasons.push(`${reason} ×${multiplier.toFixed(2)}`);
    };

    if (passive === 'AFFINITY') {
      if ((params.elementMultiplier ?? 1) > 1) applyScore(this.config.affinityAdvantageMultiplier, 'AFFINITY: ventaja elemental');
      if (params.technique && params.technique.element === params.player.element) {
        applyScore(this.config.affinityTechniqueMultiplier, 'AFFINITY: técnica afín');
      }
    }

    if (passive === 'BREACH') {
      const gap = this.keyAttributeGap(params.player.stats, params.opponent?.stats, effectiveAction);
      if (gap !== undefined && gap >= this.config.breachGap) {
        applyScore(this.config.breachMultiplier, `BREACH: brecha ${gap.toFixed(1)}`);
      }
    }

    if (passive === 'COUNTER'
      && params.context.secondsSincePossessionGain >= 0
      && params.context.secondsSincePossessionGain <= this.config.counterWindowSeconds) {
      if ((COUNTER_ACTIONS.includes(params.actionType) || COUNTER_ACTIONS.includes(effectiveAction))
        && (params.context.isVerticalAction || effectiveAction !== 'PASS')) {
        applyScore(this.config.counterActionMultiplier, 'COUNTER: transición rápida');
      }
      if (params.actionType === 'MAKE_RUN' || params.actionType === 'DRIBBLE' || params.actionType === 'CARRY') {
        result.movementSpeedMultiplier = this.config.counterRunSpeedMultiplier;
        result.debugReasons.push(`COUNTER: velocidad ofensiva ×${result.movementSpeedMultiplier.toFixed(2)}`);
      }
    }

    if (passive === 'DIRTY_PLAY') {
      if (DIRTY_DUELS.includes(effectiveAction)) applyScore(this.config.dirtyDuelMultiplier, 'DIRTY_PLAY: duelo agresivo');
      else if (effectiveAction === 'PRESS' || effectiveAction === 'MARK_PLAYER') {
        applyScore(this.config.dirtyPressMultiplier, 'DIRTY_PLAY: presión agresiva');
      }
      if (DIRTY_DUELS.includes(effectiveAction) || effectiveAction === 'PRESS' || effectiveAction === 'MARK_PLAYER') {
        result.foulRiskMultiplier = this.config.dirtyFoulRiskMultiplier;
        result.debugReasons.push(`DIRTY_PLAY: riesgo de falta ×${result.foulRiskMultiplier.toFixed(2)}`);
      }
    }

    if (passive === 'JUSTICE') {
      const adversity = params.context.scoreDiff < 0
        || (params.context.secondsSinceConceded >= 0
          && params.context.secondsSinceConceded <= this.config.justiceConcededWindowSeconds);
      if (adversity && (DEFENSIVE_ACTIONS.includes(effectiveAction)
        || (params.actionType === 'USE_TECHNIQUE' && params.context.isDecisive))) {
        applyScore(this.config.justiceDefensiveMultiplier, 'JUSTICE: respuesta defensiva');
      }
      if (params.opponent?.passive === 'DIRTY_PLAY'
        && (DIRTY_DUELS.includes(effectiveAction) || effectiveAction === 'DRIBBLE')) {
        applyScore(this.config.justiceVsDirtyMultiplier, 'JUSTICE: duelo contra DIRTY_PLAY');
      }
    }

    if (passive === 'TENSION' && this.isCritical(params.context)) {
      if (IMPORTANT_ACTIONS.includes(params.actionType)) {
        applyScore(this.config.tensionActionMultiplier, 'TENSION: momento decisivo');
        result.staminaCostMultiplier = this.config.tensionStaminaCostMultiplier;
        result.debugReasons.push(`TENSION: coste de stamina ×${result.staminaCostMultiplier.toFixed(2)}`);
      }
      result.techniqueAggressionMultiplier = this.config.tensionTechniqueAggressionMultiplier;
      result.debugReasons.push(`TENSION: agresividad técnica ×${result.techniqueAggressionMultiplier.toFixed(2)}`);
    }

    return result;
  }

  isCounterWindowActive(player: PassivePlayer, secondsSincePossessionGain: number): boolean {
    return player.passive === 'COUNTER' && secondsSincePossessionGain >= 0
      && secondsSincePossessionGain <= this.config.counterWindowSeconds;
  }

  private isCritical(context: PassiveContext): boolean {
    return context.minute >= this.config.tensionMinute || Math.abs(context.scoreDiff) <= 1 || context.isDecisive === true;
  }

  private keyAttributeGap(
    player: PlayerStats,
    opponent: PlayerStats | undefined,
    action: PassiveActionType,
  ): number | undefined {
    if (!opponent) return undefined;
    if (action === 'SHOOT') return player.kick - opponent.defense;
    if (action === 'DRIBBLE' || action === 'CARRY') return (player.dribble + player.control) / 2 - opponent.defense;
    if (action === 'PASS') return player.pass - (opponent.defense + opponent.control) / 2;
    if (action === 'TACKLE' || action === 'INTERCEPT') return player.defense - (opponent.control + opponent.dribble) / 2;
    if (action === 'SAVE') return player.defense - opponent.kick;
    if (action === 'DUEL') return player.power - opponent.power;
    return undefined;
  }
}

const defaultPassiveSystem = new PassiveSystem();

export function applyPassiveModifier(params: PassiveModifierParams): PassiveModifierResult {
  return defaultPassiveSystem.applyPassiveModifier(params);
}
